'use client';

import {
  createContext, useContext, useState, ReactNode, useMemo,
} from 'react';

export type CompanyApplicantView = 'rank' | 'flow';

interface CompanyApplicantContextType {
  selectedJob: ApiSchema<'MyJobCard'> | null;
  setSelectedJob: (job: ApiSchema<'MyJobCard'> | null) => void;
  currentView: CompanyApplicantView;
  setCurrentView: (view: CompanyApplicantView) => void;
  /** Slug pre-loaded from the URL (for bookmark navigation). Null once a job card is clicked. */
  initialSlug: string | null;
  /** Status filter to apply when the next job is loaded (set by tag clicks or URL params). */
  initialStatus: number | null;
  setInitialStatus: (status: number | null) => void;
}

const CompanyApplicantContext = createContext<CompanyApplicantContextType | undefined>(undefined);

/**
 * Provides selected job + view mode (rank/flow) for the company applicant panel inside Mission Control.
 * Bulk selection state lives in ApplicantListContent alongside its form ref.
 *
 * @param initialSlug - job slug pre-loaded from the URL (bookmark navigation)
 * @param initialStatus - applicant status filter pre-loaded from the URL
 */
export const CompanyApplicantProvider = ({
  children,
  initialSlug = null,
  initialStatus: initialStatusProp = null,
}: {
  children: ReactNode;
  initialSlug?: string | null;
  initialStatus?: number | null;
}) => {
  const [selectedJob, setSelectedJob] = useState<ApiSchema<'MyJobCard'> | null>(null);
  const [currentView, setCurrentView] = useState<CompanyApplicantView>('rank');
  const [initialStatus, setInitialStatus] = useState<number | null>(initialStatusProp);

  const value = useMemo(() => ({
    selectedJob,
    setSelectedJob,
    currentView,
    setCurrentView,
    initialSlug,
    initialStatus,
    setInitialStatus,
  }), [selectedJob, currentView, initialSlug, initialStatus]);

  return (
    <CompanyApplicantContext.Provider value={value}>
      {children}
    </CompanyApplicantContext.Provider>
  );
};

export const useCompanyApplicant = () => {
  const context = useContext(CompanyApplicantContext);
  if (context === undefined) {
    throw new Error('useCompanyApplicant must be used within a CompanyApplicantProvider');
  }
  return context;
};
